import { IconButton, ListItemText } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { VyIcon } from '@vy/ui-design-system';
import React from 'react';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../components/new-design/feedback/VyDialog';
import { useDeleteCustomer } from '../../../../hooks/requests/use-delete-customer';
import { useDialog } from '../../../../hooks/use-dialog';
import { runMutation } from '../../../../utils/vy-mutation';
import {
  invalidateCustomersQueries,
  invalidateInvoicesQueries,
} from '../../../../utils/query-invalidation';

type DeleteCustomerMenuItemProps = {
  customerId: string;
  disabled?: boolean;
  customerName?: string;
};

export const DeleteCustomerMenuItem: React.FC<DeleteCustomerMenuItemProps> = ({
  customerId,
  disabled,
  customerName,
}) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const deleteCustomerMutation = useDeleteCustomer();
  const queryClient = useQueryClient();

  const handleDialogSubmit = async () => {
    handleClose();
    await runMutation(
      deleteCustomerMutation,
      customerId,
      {
        pending: 'Deleting...',
        success: 'Deleted',
        error: 'Failed to delete customer',
      },
      {
        onSuccess: async () => {
          // Invalidate all queries in parallel for better performance
          await Promise.all([
            invalidateCustomersQueries(queryClient),
            invalidateInvoicesQueries(queryClient),
          ]);
        },
      },
    );
  };

  return (
    <>
      <div>
        <MenuItem disabled={disabled} onClick={handleOpen} sx={{ padding: '5px 8px' }}>
          <IconButton
            sx={{
              position: 'sticky',
              top: '10px',
              '&:hover': {
                background: 'none',
              },
            }}
          >
            <VyIcon
              icon="Delete"
              style={{
                transform: 'scale(0.8)',
              }}
            />
          </IconButton>
          <ListItemText>Delete</ListItemText>
        </MenuItem>

        <VyDialog
          open={isDialogOpen}
          onClose={handleClose}
          label={`Delete ${customerName}`}
          labelDescription={`Are you sure you want to delete ${customerName}?`}
          warningText="Once done, you will not be able to recover or resume these items"
          cancelButtonText="Cancel"
          ctaText="Delete"
          ctaDisabled={deleteCustomerMutation.isLoading}
          onSubmit={handleDialogSubmit}
        />
      </div>
    </>
  );
};
